import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { companyInfo } from '../data/mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('https://formspree.io/f/mojzaapr', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', email: '', phone: '', address: '', message: '' });
        }, 5000);
      } else {
        setError('Something went wrong. Please try again or call us directly.');
      }
    } catch (err) {
      setError('Unable to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="romano-badge mb-4 inline-block">Contact Us</span>
          <h2 className="romano-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#622B62] mb-4">
            Get Your Free Estimate
          </h2>
          <div className="section-divider mx-auto mb-6" />
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Ready to improve your home's comfort? Contact us today for a free,
            no-obligation estimate.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <div className="bg-[#622B62] rounded-3xl p-8 sm:p-10 text-white relative overflow-hidden">
              {/* Decorative circles */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative z-10">
                <h3 className="romano-heading text-2xl font-bold mb-6">
                  Contact Information
                </h3>
                
                <div className="space-y-6">
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="flex items-start gap-4 hover:translate-x-2 transition-transform"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#DA9C3A] flex items-center justify-center flex-shrink-0">
                      <Phone size={20} />
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">Call Us</p>
                      <p className="text-lg font-semibold">{companyInfo.phone}</p>
                    </div>
                  </a>
                  
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-start gap-4 hover:translate-x-2 transition-transform"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#DA9C3A] flex items-center justify-center flex-shrink-0">
                      <Mail size={20} />
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">Email Us</p>
                      <p className="text-lg font-semibold">{companyInfo.email}</p>
                    </div>
                  </a>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#DA9C3A] flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} />
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">Visit Us</p>
                      <p className="text-lg font-semibold">{companyInfo.address}</p>
                    </div>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="mt-10 pt-8 border-t border-white/20">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock size={20} className="text-[#DA9C3A]" />
                    <h4 className="font-semibold">Business Hours</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {companyInfo.businessHours.map((item) => (
                      <div key={item.day} className="flex justify-between">
                        <span className="text-white/70">{item.day}</span>
                        <span className={item.hours === 'Closed' ? 'text-[#DA9C3A]' : ''}>
                          {item.hours}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Google Map */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <MapPin size={18} className="text-[#DA9C3A]" />
                    Find Us
                  </h4>
                  <div className="rounded-xl overflow-hidden border-2 border-white/10">
                    <iframe
                      title="Romano Heating & Cooling Location"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3115.5!2d-121.298!3d38.744!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809b250a1c79b9f5%3A0x7e8f4d3b2a1c9e50!2sRomano+Heating+%26+Cooling!5e0!3m2!1sen!2sus!4v1"
                      width="100%"
                      height="180"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#FDFCF7] rounded-3xl p-8 sm:p-10 shadow-lg border border-gray-100">
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-10">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
                  <CheckCircle size={40} className="text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-[#622B62] mb-3">
                  Thank You!
                </h3>
                <p className="text-gray-600">
                  We've received your message and will get back to you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#622B62] font-medium">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input border-gray-200 focus:border-[#622B62] rounded-lg py-3"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#622B62] font-medium">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input border-gray-200 focus:border-[#622B62] rounded-lg py-3"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[#622B62] font-medium">
                      Phone *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="form-input border-gray-200 focus:border-[#622B62] rounded-lg py-3"
                      placeholder="(916) 555-0123"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-[#622B62] font-medium">
                      Address
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className="form-input border-gray-200 focus:border-[#622B62] rounded-lg py-3"
                      placeholder="City, ZIP Code"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#622B62] font-medium">
                    How can we help? *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="form-input border-gray-200 focus:border-[#622B62] rounded-lg resize-none"
                    placeholder="Describe your HVAC needs..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                  className="w-full bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-bold py-6 rounded-full text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-60"
                >
                  <Send size={20} className="mr-2" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>

                {error && (
                  <p data-testid="contact-error" className="text-center text-sm text-red-500 font-medium">
                    {error}
                  </p>
                )}

                <p className="text-center text-sm text-gray-500">
                  By submitting, you agree to our privacy policy.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
